/**
 * Theme tokens for the demo subtree — the brand accent and the chart palette.
 *
 * The demo runs inside the Migros Bank shell, whose accent is the dark brand
 * green (`--primary: 164 58% 19%`). Compass carries its own accent, #1b4dff,
 * so the `--primary` family is re-declared here rather than in
 * `migrosbank-colors.css`: this subtree is the only place that changes, and the
 * rest of the app keeps the brand green.
 *
 * `--foreground` is NOT `var(--primary)` at this level on purpose. The root
 * declares it that way, but a custom property resolves its `var()` where it is
 * declared and inherits the resolved colour, so overriding `--primary` here
 * would leave every line of body text the old green ink. It is re-declared as a
 * blue-tinted near-black instead, and `--border` / `--input` likewise lose the
 * green tint they inherit from the shell.
 *
 * Defined as CSS custom properties on a wrapper rather than as Tailwind classes
 * so the same token works in `fill=`/`stroke=` on inline SVG and in `style` on
 * HTML marks, and so light/dark is one media/class swap instead of a prop
 * threaded through every chart.
 *
 * Values are the validated palette — see `palette.ts` for the validator output.
 *
 * This is also the demo's design-token layer: typeface, radius ladder, elevation
 * ladder and neutral surfaces. They live here rather than in `app/globals.css`
 * because that file is shared by the whole multi-client banking platform — the
 * demo must not restyle the bank. The standalone copy at `~/Dev/compass-demo`
 * gets the same tokens from the same file.
 *
 * Radix portals (`LmsShell`'s Dialog, the tour popover, every `Select`) mount on
 * `document.body` and therefore escape the `[data-lp-charts]` subtree, so the
 * selector list repeats for them. `[data-lp-portal]` is ours to set;
 * `[data-radix-popper-content-wrapper]` is Radix's own and is safe to claim here
 * because this `<style>` only exists while the demo is mounted, and the demo owns
 * its whole route.
 *
 * The wrapper is `display: contents` and that is LOAD-BEARING, not tidiness.
 * The page relies on an unbroken `h-full` chain from a `h-screen
 * overflow-hidden` body down to its own `overflow-y-auto` scroll container.
 * A plain `<div>` here has auto height, so `h-full` beneath it resolved against
 * an indefinite parent, the scroll container grew to its full content height,
 * `overflow-y-auto` never engaged, and the page became unscrollable — content
 * below the fold was silently clipped by the body. `display: contents` makes
 * this element generate no box at all while custom properties still inherit
 * through it, so the theme is applied without touching layout.
 */
import "@fontsource-variable/inter"

export default function ChartTheme({ children }: { children: React.ReactNode }) {
    return (
        <>
            <style>{`
                [data-lp-charts] {
                    display: contents;
                }

                [data-lp-charts],
                [data-lp-portal],
                [data-radix-popper-content-wrapper] {
                    font-family: var(--lp-font-sans);

                    /* The shell paints body ink with the resolved
                       \`--color-foreground\`, so re-declaring the token alone
                       leaves every heading in the old green. Inheriting
                       \`color\` from here re-inks the subtree; \`display:
                       contents\` does not block inheritance. */
                    color: hsl(var(--foreground));

                    /* Compass accent — #1b4dff */
                    --primary: 226.8 100% 55.3%;
                    --primary-hover: 226.8 100% 45%;
                    --primary-bold2: 226.8 100% 45%;
                    --primary-foreground: 0 0% 100%;
                    --primary-light: 226.8 100% 65%;
                    --primary-medium: 226.8 40% 60%;
                    --accent: 226.8 30% 95%;
                    --accent-foreground: 226.8 100% 45%;
                    --foreground: 226.8 30% 12%;
                    --border: 226.8 20% 89%;
                    --input: 226.8 20% 89%;

                    /* Neutrals on the accent's hue. The shell's greys are
                       Migros-green-tinted (240 / 160), which reads as a colour
                       cast under a blue accent. Lightness is held so contrast
                       does not move: muted-foreground goes 4.83:1 -> 5.02:1 on
                       white, and 4.39:1 -> 4.54:1 on muted — the old pair
                       failed AA, this one passes. */
                    --background: 0 0% 100%;
                    --card: 0 0% 100%;
                    --card-foreground: 226.8 30% 12%;
                    --popover: 0 0% 100%;
                    --popover-foreground: 226.8 30% 12%;
                    --muted: 226.8 24% 96%;
                    --muted-foreground: 226.8 10% 46%;
                    --secondary: 226.8 24% 96%;
                    --secondary-foreground: 226.8 30% 12%;

                    /* Typeface. Self-hosted via \`@fontsource-variable/inter\`
                       (imported below) rather than a Google Fonts link — the
                       standalone is a static site and a blocking third-party
                       font request is a visible FOUT during a live demo. */
                    --lp-font-sans:
                        "Inter Variable", ui-sans-serif, system-ui, -apple-system,
                        "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                    --font-sans: var(--lp-font-sans);

                    /* Radius ladder. \`--radius-xl\` was never defined, so
                       \`rounded-xl\` — the card radius, 25 uses — fell through to
                       Tailwind's built-in 0.75rem and was the one radius NOT
                       derived from \`--radius\`. The calc reproduces 0.75rem
                       exactly, so nothing moves; it is now tokenised. */
                    --radius: 0.5rem;
                    --radius-xl: calc(var(--radius) + 0.25rem);
                    --radius-2xl: calc(var(--radius) + 0.5rem);

                    /* Elevation ladder. The demo had 14 ad-hoc shadow
                       declarations across ~2,900 lines and four of its six large
                       section cards had none — depth was a 1px border and
                       nothing else, which is what reads as a wireframe. Three
                       levels, one transition. */
                    --lp-elev-1:
                        0 1px 2px 0 rgb(16 24 40 / 0.04),
                        0 1px 3px 0 rgb(16 24 40 / 0.06);
                    --lp-elev-2:
                        0 2px 4px -1px rgb(16 24 40 / 0.06),
                        0 4px 12px -2px rgb(16 24 40 / 0.08);
                    --lp-elev-3: 0 8px 24px -4px rgb(16 24 40 / 0.12);
                    --lp-elev-transition:
                        box-shadow 150ms ease, border-color 150ms ease;

                    /* Tailwind v4 resolves \`hsl(var(--primary))\` into
                       \`--color-primary\` at \`:root\`, and a resolved custom
                       property inherits as a colour, not as the \`var()\` that
                       produced it. Re-declaring only the triplets above would
                       therefore change nothing a utility class renders — the
                       \`--color-*\` layer has to be re-derived here as well. */
                    --color-primary: hsl(var(--primary));
                    --color-primary-hover: hsl(var(--primary-hover));
                    --color-primary-bold2: hsl(var(--primary-bold2));
                    --color-primary-foreground: hsl(var(--primary-foreground));
                    --color-primary-light: hsl(var(--primary-light));
                    --color-primary-medium: hsl(var(--primary-medium));
                    --color-accent: hsl(var(--accent));
                    --color-accent-foreground: hsl(var(--accent-foreground));
                    --color-foreground: hsl(var(--foreground));
                    --color-border: hsl(var(--border));
                    --color-input: hsl(var(--input));
                    --color-background: hsl(var(--background));
                    --color-card: hsl(var(--card));
                    --color-card-foreground: hsl(var(--card-foreground));
                    --color-popover: hsl(var(--popover));
                    --color-popover-foreground: hsl(var(--popover-foreground));
                    --color-muted: hsl(var(--muted));
                    --color-muted-foreground: hsl(var(--muted-foreground));
                    --color-secondary: hsl(var(--secondary));
                    --color-secondary-foreground: hsl(var(--secondary-foreground));

                    --lp-pflicht: #b3261e;
                    --lp-voraussetzung: #7c5fb0;
                    --lp-luecke: #b45309;
                    --lp-ziel: #1d4ed8;

                    --lp-critical: #b3261e;
                    --lp-warning: #b45309;
                    --lp-good: #15803d;

                    --lp-seq: #1b4dff;
                    --lp-track: color-mix(in srgb, currentColor 10%, transparent);
                    --lp-grid: color-mix(in srgb, currentColor 14%, transparent);
                }
                /*
                 * Elevation utilities. Plain CSS classes rather than \`@theme\`
                 * tokens because this stylesheet is injected at runtime and
                 * Tailwind can only generate utilities at build time — and the
                 * same file has to serve the Next route and the standalone Vite
                 * build, which have different build-time CSS entry points.
                 */
                [data-lp-charts] .lp-card,
                [data-lp-portal].lp-card {
                    box-shadow: var(--lp-elev-1);
                }

                [data-lp-charts] .lp-card-hover {
                    transition: var(--lp-elev-transition);
                }

                @media (hover: hover) {
                    [data-lp-charts] .lp-card-hover:hover {
                        box-shadow: var(--lp-elev-2);
                        border-color: hsl(var(--primary) / 0.35);
                    }
                }

                @media (prefers-reduced-motion: reduce) {
                    [data-lp-charts] .lp-card-hover {
                        transition: none;
                    }
                }

                .dark [data-lp-charts],
                [data-chat-theme="dark"] [data-lp-charts] {
                    --primary: 226.8 100% 66%;
                    --primary-hover: 226.8 100% 72%;
                    --primary-bold2: 226.8 100% 72%;
                    --primary-foreground: 226.8 60% 10%;
                    --primary-light: 226.8 100% 76%;
                    --primary-medium: 226.8 30% 62%;
                    --accent: 226.8 25% 20%;
                    --accent-foreground: 226.8 100% 80%;
                    --foreground: 226.8 25% 96%;
                    --border: 226.8 18% 24%;
                    --input: 226.8 18% 24%;

                    --color-primary: hsl(var(--primary));
                    --color-primary-hover: hsl(var(--primary-hover));
                    --color-primary-bold2: hsl(var(--primary-bold2));
                    --color-primary-foreground: hsl(var(--primary-foreground));
                    --color-primary-light: hsl(var(--primary-light));
                    --color-primary-medium: hsl(var(--primary-medium));
                    --color-accent: hsl(var(--accent));
                    --color-accent-foreground: hsl(var(--accent-foreground));
                    --color-foreground: hsl(var(--foreground));
                    --color-border: hsl(var(--border));
                    --color-input: hsl(var(--input));

                    --lp-pflicht: #dd6257;
                    --lp-voraussetzung: #9d80d0;
                    --lp-luecke: #c48232;
                    --lp-ziel: #6892e6;

                    --lp-critical: #dd6257;
                    --lp-warning: #c48232;
                    --lp-good: #4ade80;

                    --lp-seq: #7a9cf5;
                }
            `}</style>
            <div data-lp-charts>{children}</div>
        </>
    )
}
